/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import React, { useMemo, useRef, useState } from 'react'

import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Button, Dropdown } from 'antd'
import { observer } from 'mobx-react-lite'
import { NewModal } from 'src/components/NewModal'
import NewOrgForm from 'src/components/NewOrgForm'
import { font } from 'src/components/css'
import { createOrg, editOrg } from 'src/helpers/EntityCreationHelper'
import { useMobxStore } from 'src/state'
import Page from '../../components/Page'
import OrgList from './OrgList'

const Organizations = () => {
  const [orgCount, setOrgCount] = useState('')
  const [favCount, setFavCount] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [initialState, setInitialState] = useState(null)
  const [reloadWindowAfterCreate, setReloadWindowAfterCreate] = useState(false)
  const orgModalRef = useRef(null)

  const {
    me: {
      state: { account, me },
    },
  } = useMobxStore()

  const triggerRefresh = () => {
    setRefreshKey((key) => ++key)
  }

  const onAddOrg = async (...args) => {
    const [values] = args
    const { imageData, ...form } = values

    // Duplicate name_full since API requires it
    form.name = form.name_full
    form.can_contain_organizations = true
    form.can_contain_teams = true

    if (typeof form.is_public === 'undefined') {
      form.is_public = true
    }

    const body = JSON.stringify(form)
    const response = await req('/organizations', {
      method: 'POST',
      body,
    })

    if (imageData) {
      await req(`/organizations/${response.data.id}/image`, {
        method: 'POST',
        body: imageData,
      })
    }

    triggerRefresh()
    return response
  }

  const items = [
    {
      key: '1',
      label: (
        <a
          target='_blank'
          rel='noopener noreferrer'
          onClick={() => {
            setReloadWindowAfterCreate(true)
            setInitialState(null)
            orgModalRef.current.openModal()
          }}
        >
          Create Organization
        </a>
      ),
    },
    {
      to: `${ROUTES.ORGS.FAVOURITES}`,
      text: `following ${favCount ? `(${favCount})` : ''}`,
      component: (
        <OrgList
          key='favs'
          slug='/favorites/organizations'
          setCount={setFavCount}
        />
      ),
    },
  ]

  const actions =
    isCanlan || me?.account_type === 1 || me?.account_id !== null
      ? []
      : [
          <Dropdown
            menu={{ items }}
            placement='bottomLeft'
            key='add'
            trigger={['click']}
          >
            <Button
              css={css`
                margin-left: 16px;
                display: flex;
                justify-content: center;
                align-items: center;
                width: ${64}px;
                height: 32px;

                border: ${isCanlan ? 2 : 1}px solid ${colors.DEFAULT_FLAIR};
                border-radius: 4px;

                ${font.title}
                font-weight: 600;
                font-size: 18px;

                letter-spacing: 0.015em;
                text-transform: uppercase;
                color: white;

                ${isCanlan && 'padding-bottom: 2px;'}

                transition: border-color 0.1s ease-in-out;

                &:hover {
                  border-color: white;
                }
              `}
            >
              + NEW
            </Button>
          </Dropdown>,
        ]

  const pages = useMemo(
    () => [
      {
        to: `${ROUTES.ORGS.ROOT}/my`,
        text: `${t('common:organization', { count: 2 })} ${
          orgCount ? `(${orgCount})` : ''
        }`,
        component: (
          <OrgList
            key='orgs'
            slug='/users/me/organizations'
            setCount={setOrgCount}
            refreshKey={refreshKey}
            setInitialState={setInitialState}
            onEditOrg={() => {
              orgModalRef.current.openModal()
            }}
          />
        ),
        actions: actions,
      },
      {
        to: `${ROUTES.ORGS.FAVOURITES}`,
        text: 'following',
        component: (
          <OrgList
            key='favs'
            slug='/favorites/organizations'
            setCount={setFavCount}
            setInitialState={setInitialState}
            onEditOrg={() => {
              orgModalRef.current.openModal()
            }}
          />
        ),
      },
    ],
    [
      t,
      orgCount,
      favCount,
      refreshKey,
      actions,
      setOrgCount,
      setFavCount,
      setInitialState,
      orgModalRef,
    ]
  )

  const BASE_ROUTE = ROUTES.ORGS.ROOT

  return (
    <>
      <Page
        baseRoute={BASE_ROUTE}
        pages={pages}
        baseTitle={t('common:organization', { count: 2 })}
        title={t('util:capitalize', {
          text: t('common:organization', { count: 2 }),
        })}
      />
      <NewModal ref={orgModalRef} shouldCloseOnOverlayClick={true}>
        <div
          css={css`
            width: 50vw;
            background: #26303e;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            transition: all 0.1s ease-in-out;
            border-radius: 8px;
            padding-top: 16px;
          `}
        >
          <NewOrgForm
            initialState={initialState || null}
            onSubmit={initialState ? editOrg : createOrg}
            onCancel={() => {
              setInitialState(null)
              orgModalRef.current.closeModal()
            }}
            triggerRefresh={triggerRefresh}
            reloadPage={reloadWindowAfterCreate}
            account={account}
            orgModalRef={orgModalRef}
          />
        </div>
      </NewModal>
    </>
  )
}

export default observer(Organizations)
